import { supabase } from '@/integrations/supabase/client';
import { PRIVACY_VERSION, TERMS_VERSION } from '@/lib/metaPixel';

const PENDING_KEY = 'ngs_pending_signup_consent';

export interface PendingSignupConsent {
  adult: boolean;
  representative: boolean;
  acceptedAt: string;
  method?: string;
}

/** Store the consent given right before an OAuth redirect or email confirmation. */
export function storePendingSignupConsent(consent: Omit<PendingSignupConsent, 'acceptedAt'>) {
  try {
    localStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ ...consent, acceptedAt: new Date().toISOString() })
    );
  } catch {
    /* storage unavailable */
  }
}

function readPending(): PendingSignupConsent | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PendingSignupConsent;
  } catch {
    return null;
  }
}

function clearPending() {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Persist the consent captured before an OAuth sign-in, once the session
 * exists. Skips silently when the user already has consent on record.
 */
export async function flushPendingSignupConsent(userId: string) {
  const pending = readPending();
  if (!pending) return;
  clearPending();

  try {
    const { data: existing } = await supabase
      .from('consent_log')
      .select('id')
      .eq('user_id', userId)
      .eq('consent_type', 'terms')
      .limit(1);
    if (existing && existing.length > 0) return;

    const method = pending.method ?? 'google';
    await supabase.from('consent_log').insert([
      {
        user_id: userId,
        consent_type: 'age_verification',
        granted: true,
        version: TERMS_VERSION,
        metadata: {
          adult: pending.adult,
          representative: pending.representative,
          method,
          accepted_at: pending.acceptedAt,
        },
      },
      {
        user_id: userId,
        consent_type: 'privacy_policy',
        granted: true,
        version: PRIVACY_VERSION,
        metadata: { method, accepted_at: pending.acceptedAt },
      },
      {
        user_id: userId,
        consent_type: 'terms',
        granted: true,
        version: TERMS_VERSION,
        metadata: { method, accepted_at: pending.acceptedAt },
      },
    ]);
  } catch {
    // Non-blocking: sign-in must not fail because of consent logging.
  }
}
